'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface SavedSearchFilter {
  id: string;
  name: string;
  query: string;
  category: string;
  province: string;
  priceRange: string;
  adventureLevel: string;
  season: string;
  duration: string;
  sortBy: string;
  createdAt: string;
}

const LS_KEY = 'yn_saved_searches';

function readFromStorage(): SavedSearchFilter[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeToStorage(filters: SavedSearchFilter[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(filters));
  } catch {}
}

export function useSavedSearches(userId?: string | null) {
  const [savedSearches, setSavedSearches] = useState<SavedSearchFilter[]>([]);
  const [syncing, setSyncing] = useState(false);

  // Load from localStorage on mount, then merge with Supabase if logged in
  useEffect(() => {
    const local = readFromStorage();
    setSavedSearches(local);

    if (!userId) return;

    // Merge with Supabase saved searches
    const supabase = createClient();
    supabase
      .from('user_preferences')
      .select('saved_searches')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error || !data) return;
        try {
          const remote: SavedSearchFilter[] = JSON.parse(data.saved_searches || '[]');
          if (!Array.isArray(remote) || remote.length === 0) return;
          // Merge: remote takes precedence, deduplicate by id
          const merged = [...remote];
          local.forEach(l => {
            if (!merged.find(r => r.id === l.id)) merged.push(l);
          });
          merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          setSavedSearches(merged);
          writeToStorage(merged);
        } catch {}
      });
  }, [userId]);

  const persistToSupabase = useCallback(async (filters: SavedSearchFilter[], uid: string) => {
    const supabase = createClient();
    try {
      await supabase
        .from('user_preferences')
        .upsert(
          { user_id: uid, saved_searches: JSON.stringify(filters) },
          { onConflict: 'user_id' }
        );
    } catch {
      // Silently fail — localStorage is the source of truth
    }
  }, []);

  const saveSearch = useCallback(
    async (name: string, filters: Omit<SavedSearchFilter, 'id' | 'name' | 'createdAt'>) => {
      const newEntry: SavedSearchFilter = {
        id: `ss-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: name.trim(),
        createdAt: new Date().toISOString(),
        ...filters,
      };
      setSyncing(true);
      setSavedSearches(prev => {
        const updated = [newEntry, ...prev].slice(0, 20); // max 20 saved searches
        writeToStorage(updated);
        if (userId) persistToSupabase(updated, userId);
        return updated;
      });
      setSyncing(false);
      return newEntry;
    },
    [userId, persistToSupabase]
  );

  const deleteSearch = useCallback(
    async (id: string) => {
      setSavedSearches(prev => {
        const updated = prev.filter(s => s.id !== id);
        writeToStorage(updated);
        if (userId) persistToSupabase(updated, userId);
        return updated;
      });
    },
    [userId, persistToSupabase]
  );

  const renameSearch = useCallback(
    async (id: string, newName: string) => {
      setSavedSearches(prev => {
        const updated = prev.map(s => (s.id === id ? { ...s, name: newName.trim() } : s));
        writeToStorage(updated);
        if (userId) persistToSupabase(updated, userId);
        return updated;
      });
    },
    [userId, persistToSupabase]
  );

  return { savedSearches, saveSearch, deleteSearch, renameSearch, syncing };
}
